/**
 * Block reordering.
 *
 * Almost every "move things around" preset is the same operation at a different
 * scale: chop the pattern into equal blocks — bars, half bars, beats or single
 * steps — and play those blocks in a different order. Writing that once means a
 * new variation is a name and an order function, not another hand-rolled loop.
 *
 * An order array answers "which block should be heard at position i", so
 * `[0, 1, 3, 2]` swaps the third and fourth blocks.
 */

export type OrderFn = (count: number) => number[];

const identity = (n: number) => Array.from({ length: n }, (_, i) => i);

/** Exchanges two blocks, leaving the rest alone. */
export function swapBlocks(a: number, b: number): OrderFn {
  return (n) => {
    const o = identity(n);
    if (a < n && b < n) [o[a], o[b]] = [o[b], o[a]];
    return o;
  };
}

/** Shifts everything along by `by` blocks, wrapping. Negative goes backwards. */
export function rotateBlocks(by: number): OrderFn {
  return (n) => identity(n).map((_, i) => ((i + by) % n + n) % n);
}

export function reverseBlocks(): OrderFn {
  return (n) => identity(n).reverse();
}

export function shuffleBlocks(): OrderFn {
  return (n) => {
    const o = identity(n);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [o[i], o[j]] = [o[j], o[i]];
    }
    return o;
  };
}

/** Swaps every neighbouring pair: 2 1 4 3 6 5. */
export function swapPairs(): OrderFn {
  return (n) => identity(n).map((_, i) => (i % 2 === 0 ? Math.min(i + 1, n - 1) : i - 1));
}

/** Each block heard twice before moving on: 1 1 2 2. */
export function doubleUp(): OrderFn {
  return (n) => identity(n).map((_, i) => Math.floor(i / 2) % n);
}

/** Out and back: 1 2 2 1. */
export function mirrorBlocks(): OrderFn {
  return (n) => identity(n).map((_, i) => (i < n / 2 ? i : n - 1 - i));
}

/** Ends first, working inwards: 1 4 2 3. */
export function outsideIn(): OrderFn {
  return (n) => identity(n).map((_, i) => (i % 2 === 0 ? i / 2 : n - 1 - (i - 1) / 2));
}

/** Every block replaced by one of them — turns four bars into a one-bar loop. */
export function lockTo(index: number): OrderFn {
  return (n) => {
    const pick = index < 0 ? Math.max(0, n + index) : Math.min(index, n - 1);
    return identity(n).map(() => pick);
  };
}

/** Bounces between two blocks: 1 3 1 3. */
export function alternate(a: number, b: number): OrderFn {
  return (n) => identity(n).map((_, i) => Math.min(i % 2 === 0 ? a : b, n - 1));
}

/** Keeps the odd-numbered blocks, dropping the rest: 1 3 1 3 for n=4. */
export function everyOther(): OrderFn {
  return (n) => identity(n).map((_, i) => (Math.floor(i / 2) * 2) % n);
}
